import { AlertTriangle, MapPin, Package } from 'lucide-react'
import React from 'react'

import { useAreaState } from '../stores/useAreaStore'

import { WarehousePosition } from '@/types/warehouse'


const AreaGrid: React.FC = () => {
  const { loading, selectedArea } = useAreaState()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">加载库位信息中...</div>
      </div>
    )
  }

  if (selectedArea?.positions.length === 0) {
    return (
      <div className="text-center py-12">
        <MapPin className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">暂无库位</h3>
        <p className="mt-1 text-sm text-gray-500">请先创建库区来生成库位</p>
      </div>
    )
  }

  const getLocationStatusColor = (location: WarehousePosition) => {
    if (location.pallets.length >= location.maxCapacity) {
      return 'bg-red-100 border-red-300 text-red-700'
    }
    if (location.pallets.length > 0) {
      return 'bg-blue-100 border-blue-300 text-blue-700'
    }
    return 'bg-green-100 border-green-300 text-green-700'
  }

  const getLocationStatusIcon = (location: WarehousePosition) => {
    if (location.pallets.length >= location.maxCapacity) {
      return <Package className="h-4 w-4" />
    }
    if (location.pallets.length > 0) {
      return <AlertTriangle className="h-4 w-4" />
    }
    return <MapPin className="h-4 w-4" />
  }

  const getLocationStatusText = (location: WarehousePosition) => {
    if (location.pallets.length >= location.maxCapacity) {
      return '已满'
    }
    if (location.pallets.length > 0) {
      return `部分占用 (${location.pallets.length}/${location.maxCapacity})`
    }
    return '空闲'
  }

  const getOccupancyRate = (location: WarehousePosition) => {
    return location.maxCapacity > 0
      ? (location.pallets.length / location.maxCapacity) * 100
      : 0
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-5 sm:p-6">
        {/* 统计信息 */}
        <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white overflow-hidden shadow rounded-lg border">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MapPin className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      总库位
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {selectedArea?.positions.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MapPin className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      空闲
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {
                        selectedArea?.positions.filter(
                          (p) => p.pallets.length === 0
                        ).length
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      部分占用
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {
                        selectedArea?.positions.filter(
                          (p) =>
                            p.pallets.length > 0 &&
                            p.pallets.length < p.maxCapacity
                        ).length
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      已满
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {
                        selectedArea?.positions.filter(
                          (p) => p.pallets.length >= p.maxCapacity
                        ).length
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 库位网格 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
          {selectedArea?.positions.map((position) => {
            const occupancyRate = getOccupancyRate(position)
            return (
              <div
                key={position.id}
                className={`relative group border-2 rounded-lg p-3 text-center hover:shadow-md transition-all duration-200 cursor-pointer ${getLocationStatusColor(
                  position
                )}`}
                title={`${
                  position.name || position.code
                } - ${getLocationStatusText(position)}`}
              >
                {/* 状态图标 */}
                <div className="flex justify-center mb-2">
                  {getLocationStatusIcon(position)}
                </div>

                {/* 库位编码 */}
                <div className="text-xs font-medium truncate">
                  {position.code}
                </div>

                {/* 库位名称（如果与编码不同） */}
                {position.name && position.name !== position.code && (
                  <div className="text-xs text-gray-600 truncate mt-1">
                    {position.name}
                  </div>
                )}

                {/* 容量信息 */}
                <div className="text-xs mt-1">
                  {position.pallets.length}/{position.maxCapacity}
                </div>

                {/* 容量进度条 */}
                {position.maxCapacity > 0 && (
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className={`h-1 rounded-full transition-all duration-200 ${
                          occupancyRate >= 100
                            ? 'bg-red-500'
                            : occupancyRate >= 80
                            ? 'bg-orange-500'
                            : occupancyRate >= 50
                            ? 'bg-yellow-500'
                            : occupancyRate > 0
                            ? 'bg-blue-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(occupancyRate, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* 悬停时显示详细信息 */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  <div className="font-medium">
                    {position.name || position.code}
                  </div>
                  <div>{getLocationStatusText(position)}</div>
                  <div>
                    容量: {position.pallets.length}/{position.maxCapacity}
                  </div>
                  <div>占用率: {occupancyRate.toFixed(1)}%</div>
                  {/* {position.description && (
                    <div className="mt-1 text-gray-300">
                      {position.description}
                    </div>
                  )} */}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default AreaGrid
